// src/components/AssignLeadDrawer.tsx
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { assignLeadInList } from '../services/leadService';
import GlobalDrawer from './GlobalDrawer';
import UserCard from './UserCard';
import { User } from 'lucide-react';

interface AssignLeadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  leadIds: string[];
  onAssignSuccess?: (assignedToUser: string, affectedLeadIds: string[]) => void;
}

const AssignLeadDrawer: React.FC<AssignLeadDrawerProps> = ({
  isOpen,
  onClose,
  leadIds,
  onAssignSuccess
}) => {
  // Get current user and team members from Redux
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const currentUser = StoreData?.userData?.userDetails;
  const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl || "https://all-user-version-4.s3.us-east-1.amazonaws.com/";
  
  // Filter and format team members - STRICTLY EXCLUDE current user
  const teamMembers = useMemo(() => {
    if (!StoreData?.userData?.userDetails?.organizationEmployee) {
      return [];
    }
    
    return StoreData.userData.userDetails.organizationEmployee
      .filter((item: any) => 
        // Only include active users that are NOT the current user (strict comparison)
        item?.isActive === true && 
        item?._id !== currentUser?._id
      )
      .map((item: any) => ({
        ...item,
        firstName: item.firstName || '',
        lastName: item.lastName || '',
        teamOrOrgName: item.team ? 
          StoreData?.userData?.userDetails?.organizationTeams?.find(
            (t: any) => t._id === item.team
          )?.name || '' : 
          currentUser?.organization?.name || ''
      }));
  }, [StoreData?.userData?.userDetails, currentUser?._id]);
  
  const handleAssignLead = async (assignToUserId: string) => {
    if (!assignToUserId || !leadIds || leadIds.length === 0) {
      toast.error('Invalid selection');
      return;
    }
    
    try {
      const loadingToast = toast.loading('Assigning lead...');
      
      const { data } = await assignLeadInList({ 
        leadIds, 
        assignToUser: assignToUserId 
      });
      
      toast.dismiss(loadingToast);
      toast.success(data?.message || 'Lead assigned successfully');
      
      if (onAssignSuccess) {
        onAssignSuccess(assignToUserId, leadIds);
      }
      
      onClose();
    } catch (error: any) {
      console.error("Error assigning lead:", error);
      toast.error(error?.response?.data?.message || 'Failed to assign lead');
    }
  };
  
  return (
    <GlobalDrawer
      openWithHeader={isOpen}
      setOpenWithHeader={onClose}
      drawerTitle="Assign Lead"
      size="xs"
      customHeader
    >
      <div className="p-0" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <label className="form-label fw-bold m-2">Select team member</label>
        
        {/* Fixed "Assign to self" section that doesn't scroll */}
        <div className="p-2 border-bottom" style={{ flex: '0 0 auto' }}>
          <div 
            className="d-flex align-items-center p-2 border rounded cursor-pointer"
            style={{ backgroundColor: '#f0f9ff', borderColor: '#90cdf4' }}
            onClick={() => handleAssignLead(currentUser?._id)}
          >
            <div 
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white me-3"
              style={{ width: 36, height: 36, flexShrink: 0 }}
            >
              {currentUser?.profile?.filePath ? (
                <img 
                  src={bucketUrl + currentUser.profile.filePath} 
                  alt="Profile" 
                  className="rounded-circle" 
                  style={{ width: 36, height: 36, objectFit: 'cover' }}
                />
              ) : (
                <User size={18} />
              )}
            </div>
            <div className="flex-grow-1">
              <div className="fw-medium">Assign to self</div>
              <div className="small text-muted">
                {currentUser?.firstName} {currentUser?.lastName}
              </div>
            </div>
          </div>
        </div>
        
        {/* Scrollable team members list */}
        <div 
          className="overflow-auto p-2"
          style={{ 
            flex: '1 1 auto',
            minHeight: 0, // Critical for proper scrolling in a flex container
          }}
        >
          {teamMembers.length > 0 ? (
            teamMembers.map((member: any) => (
              <div 
                key={member._id}
                className="d-flex align-items-center p-2 border rounded mb-2 cursor-pointer"
                onClick={() => handleAssignLead(member._id)}
                style={{ transition: 'background-color 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
              >
                <div 
                  className="d-flex align-items-center justify-content-center rounded-circle bg-info text-white me-3"
                  style={{ width: 36, height: 36, flexShrink: 0 }}
                >
                  {member.profile?.filePath ? (
                    <img 
                      src={bucketUrl + member.profile.filePath} 
                      alt="Profile" 
                      className="rounded-circle" 
                      style={{ width: 36, height: 36, objectFit: 'cover' }}
                    />
                  ) : (
                    <div>{member.firstName?.charAt(0)?.toUpperCase() || 'U'}</div>
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="fw-medium">{member.firstName} {member.lastName}</div>
                  <div className="small text-muted">
                    {member.roleKey === 'team_leader' ? 'team leader' : 
                     member.roleKey === 'admin' ? 'admin' : 'employee'}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted">
              No other team members available
            </div>
          )}
        </div>
      </div>
    </GlobalDrawer>
  );
};

export default AssignLeadDrawer;